/**
 * Seletor de tema (claro/escuro/auto) — botão de 3 estados.
 *
 * Clica e cicla: Auto → Claro → Escuro → Auto …
 *
 * O ícone mostra o estado atual:
 *   - 🖥️ Auto (segue o sistema)
 *   - ☀️ Claro fixo
 *   - 🌙 Escuro fixo
 */
import { Sun, Moon, Monitor } from 'lucide-react'
import { useTheme, type ThemeMode } from '@/hooks/useTheme'

interface Props {
  /** Mostra texto além do ícone (default: false). */
  showLabel?: boolean
  className?: string
}

const PROXIMO: Record<ThemeMode, ThemeMode> = {
  auto: 'light',
  light: 'dark',
  dark: 'auto',
}

const LABELS: Record<ThemeMode, string> = {
  auto: 'Automático',
  light: 'Claro',
  dark: 'Escuro',
}

export default function ThemeToggle({ showLabel, className }: Props) {
  const { mode, setMode, efetivo } = useTheme()

  const Icon = mode === 'auto' ? Monitor : mode === 'light' ? Sun : Moon

  return (
    <button
      type="button"
      onClick={() => setMode(PROXIMO[mode])}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-card border border-border hover:bg-card-hover transition text-xs font-bold ${className || ''}`}
      title={`Tema: ${LABELS[mode]}${mode === 'auto' ? ` (agora ${efetivo === 'light' ? 'claro' : 'escuro'})` : ''}. Clique pra trocar.`}
    >
      <Icon size={14} />
      {showLabel && <span>{LABELS[mode]}</span>}
    </button>
  )
}

/**
 * Versão expandida — radio com 3 opções. Útil pra página de Configurações.
 */
export function ThemeRadio() {
  const { mode, setMode, efetivo } = useTheme()
  const opts: { key: ThemeMode; label: string; desc: string; Icon: typeof Sun }[] = [
    { key: 'auto',  label: 'Automático', desc: 'Segue o sistema (dia/noite)', Icon: Monitor },
    { key: 'light', label: 'Claro',      desc: 'Fundo branco sempre',          Icon: Sun },
    { key: 'dark',  label: 'Escuro',     desc: 'Fundo escuro sempre',          Icon: Moon },
  ]
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
      {opts.map(({ key, label, desc, Icon }) => {
        const ativo = mode === key
        return (
          <button
            key={key}
            type="button"
            onClick={() => setMode(key)}
            className={`text-left p-3 rounded-xl border-2 transition ${
              ativo
                ? 'border-accent bg-accent/10'
                : 'border-border bg-card hover:bg-card-hover'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Icon size={16} className={ativo ? 'text-accent' : 'text-text-muted'} />
              <span className={`font-extrabold text-sm ${ativo ? 'text-accent' : 'text-text-primary'}`}>
                {label}
              </span>
              {ativo && key === 'auto' && (
                <span className="text-[9px] font-bold text-text-muted ml-auto">
                  agora: {efetivo}
                </span>
              )}
            </div>
            <div className="text-[11px] text-text-secondary">{desc}</div>
          </button>
        )
      })}
    </div>
  )
}
